import { useEffect, useState } from "react";

import type { JournalEntry, Pagination } from "@/apis/gql/types";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";
import { type FetchPolicy, gql, useQuery } from "@apollo/client";

export const JOURNAL_ENTRIES_QUERY = gql`
  query GetAllJournalEntries(
    $tenantId: String
    $page: Int
    $limit: Int
    $offset: Int
    $query: JSON
    $global: String
    $sort: String
    $order: String
  ) {
    journalEntries(
      tenantId: $tenantId
      page: $page
      limit: $limit
      offset: $offset
      query: $query
      global: $global
      sort: $sort
      order: $order
    ) {
      data {
        journalEntryId
        journalId
        tenantId
        timestamp
        status
        documentReference
        documentType
        documentNumber
        bookingText
        number
        postingEventId
        createdBy
        createdAt
        updatedAt
        deletedAt

        document {
          ... on Invoice {
            invoiceId
            invoiceNo
            totalAmount
            currency
            dueDate
            invoiceDate
            split
            createdAt
            status
            serviceDate
            documentTypeSummary
            isPaid
          }
          ... on Transaction {
            bankTxnId
            counterpartyName
            amount
            currency
            side
          }
        }

        journalLines {
          journalLineId
          journalEntryId
          journalAccountId
          taxCodeId
          costCenterId
          debit
          credit
          currency
          number
          tenantId
          createdAt
          updatedAt
          deletedAt
          journalAccount {
            journalAccountId
            tenantId
            parentAccountId
            entityId
            number
            name
            accountType
            entityType
            currency
            createdAt
            updatedAt
            deletedAt
            account {
              accountId
              accountName
              accountNo
              accountDescription
              accountType
              automatic
              parentAccountId
              taxCodeId
              min
              max
              buExport
              serviceDateExport
              faktor2
            }
            supplier {
              supplierId
              supplierName
              originalSupplierName
              parentSupplierId
              status
              imported
              createdAt
              exportedAt
              datevAccountNumber
              vatId
              taxCodeId
              isCategory
              accountId
            }
            entity {
              ... on Account {
                accountId
                accountName
                accountNo
                accountDescription
                accountType
                automatic
                parentAccountId
                taxCodeId
                min
                max
                buExport
                serviceDateExport
                faktor2
              }
              ... on Supplier {
                supplierId
                supplierName
                originalSupplierName
                parentSupplierId
                status
                imported
                createdAt
                exportedAt
                datevAccountNumber
                vatId
                taxCodeId
                isCategory
                accountId
              }
              ... on BankAccount {
                bankAccountId
                name
                type
                bankConnection {
                  bankConnectionId
                  metadata {
                    id
                    bank {
                      name
                      bic
                      blz
                      location
                      city
                      isTestBank
                      popularity
                      isBeta
                      logo {
                        url
                      }
                      icon {
                        url
                      }
                      bankGroup {
                        name
                        icon {
                          url
                        }
                        logo {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          taxCode {
            taxCodeId
            name
            description
            datevBu
            vatRate
          }

          costCenter {
            costCenterId
            name
            description
            identifier
          }
        }
      }

      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
        nextPage
        previousPage
      }
    }
  }
`;

export const useGetAllJournalEntries = ({
  query,
  page,
  limit,
  offset,
  global = "",
  sort,
  order,
  fetchPolicy,
}: {
  query: ServerFilters;
  page: number;
  limit: number;
  offset: number;
  global?: string;
  sort: string;
  order: string;
  fetchPolicy?: FetchPolicy | "cache-and-network";
}) => {
  const { tenantId } = useTenant();

  const [pagination, setPagination] = useState<Pagination>({
    currentPage: page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPreviousPage: false,
    nextPage: page,
    previousPage: page > 1 ? page - 1 : 1,
  });

  const { data, loading, error, refetch } = useQuery<{
    journalEntries: {
      data: JournalEntry[];
      pagination: Pagination;
    };
  }>(JOURNAL_ENTRIES_QUERY, {
    variables: { tenantId, query, page, limit, offset, global, sort, order },
    errorPolicy: "all",
    fetchPolicy,
  });

  useEffect(() => {
    if (data?.journalEntries?.pagination && !loading) {
      setPagination(data.journalEntries.pagination);
    }
  }, [data?.journalEntries?.pagination, loading]);

  return {
    data: data?.journalEntries?.data,
    pagination,
    loading,
    error,
    refetch,
  };
};
